import { Card, Inset, Text, Flex, Badge } from "@radix-ui/themes";
import { Link } from "@inertiajs/react";
import { Heart, MapPin, Users } from "lucide-react";

const REASON_CONFIG: Record<string, { icon: React.ReactNode; label: string }> = {
  liked_event: { icon: <Heart size={12} />, label: "Liked event" },
  liked_track: { icon: <MapPin size={12} />, label: "Liked track" },
  liked_club:  { icon: <Users size={12} />, label: "Liked club" },
};

interface EventCardProps {
  id: number;
  name: string;
  date: string;
  time_until: string;
  is_past: boolean;
  track_name: string;
  track_slug: string;
  poster_url: string | null;
  club_name?: string | null;
  club_slug?: string | null;
  reasons: string[];
}

export default function EventCard({
  id,
  name,
  date,
  time_until,
  is_past,
  track_name,
  track_slug,
  poster_url,
  reasons,
}: EventCardProps) {
  return (
    <Card size="2">
      <Inset clip="padding-box" side="top" pb="current">
        {poster_url ? (
          <img
            src={poster_url}
            alt={name}
            style={{ display: "block", objectFit: "cover", width: "100%", height: 180 }}
          />
        ) : (
          <div
            style={{
              height: 180,
              background: "var(--placeholder-gradient)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "1rem",
              textAlign: "center",
            }}
          >
            <Text size="4" weight="bold" style={{ color: "var(--accent-contrast)" }}>
              {name}
            </Text>
            <Text size="2" style={{ color: "color-mix(in srgb, var(--accent-contrast) 80%, transparent)", marginTop: "0.25rem" }}>
              {date}
            </Text>
          </div>
        )}
      </Inset>

      <Text as="div" size="3" weight="bold" mb="1">
        <Link href={`/events/${id}`} style={{ color: "inherit", textDecoration: "none" }}>
          {name}
        </Link>
      </Text>

      <Text as="div" size="2" color="gray" mb="1">
        <Link href={`/tracks/${track_slug}`} style={{ color: "inherit" }}>
          @ {track_name}
        </Link>
      </Text>

      <Text as="div" size="2" color="gray" mb="2">
        {date} · {is_past ? `${time_until} ago` : `in ${time_until}`}
      </Text>

      {reasons.length > 0 && (
        <Flex gap="1" wrap="wrap" mb="2">
          {reasons.map((reason) => {
            const config = REASON_CONFIG[reason];
            if (!config) return null;
            return (
              <Badge key={reason} variant="soft" radius="full">
                <Flex align="center" gap="1">
                  {config.icon}
                  {config.label}
                </Flex>
              </Badge>
            );
          })}
        </Flex>
      )}

      <Link href={`/events/${id}`}>
        <Text size="2" className="text-accent11">
          View Event →
        </Text>
      </Link>
    </Card>
  );
}
